import { useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Field,
  Id,
  Moment,
  Notice,
  Resourceful,
  Select,
  StatusBadge,
  TextInput,
} from '../../shell/kit';
import { errorText } from '../../shell/errors';
import { useConfirm } from '../../shell/confirm';
import { useResource } from '../../shell/hooks';
import { useToast } from '../../shell/toast';
import type { CurrentUser, SiteSummary } from '../../api';
import { allowsAnywhere } from '../../api';
import { AuditTrail } from '../../shell/audit';
import type { ShellKey, Translate } from '../../i18n';
import {
  getEmployee,
  grantRole,
  grantableRoles,
  resetTotp,
  revokeRole,
  revokeSessions,
  setPassword,
  setPin,
  setStatus,
  unlock,
  updateEmployee,
  type Employee,
  type EmployeeStatus,
  type RoleName,
} from './api';
import type { StaffTranslate } from './i18n';

export interface EmployeeCardProps {
  readonly t: StaffTranslate;
  readonly shellT: Translate;
  readonly user: CurrentUser;
  readonly employeeId: string;
  readonly sites: readonly SiteSummary[];
  readonly onClose: () => void;
  readonly onChanged: () => void;
}

export function EmployeeCard(props: EmployeeCardProps) {
  const resource = useResource(() => getEmployee(props.employeeId), [props.employeeId]);
  const grantable = useResource(() => grantableRoles(), [props.employeeId]);

  return (
    <Drawer title={props.t('staff.card.profile')} onClose={props.onClose}>
      <Resourceful resource={resource}>
        {(employee) => (
          <CardBody
            {...props}
            employee={employee}
            roles={grantable.data?.roles ?? []}
            network={grantable.data?.network ?? false}
            allowedSites={grantable.data?.sites ?? []}
            onReload={() => {
              resource.reload();
              props.onChanged();
            }}
          />
        )}
      </Resourceful>
    </Drawer>
  );
}

interface CardBodyProps extends EmployeeCardProps {
  readonly employee: Employee;
  readonly roles: readonly RoleName[];
  readonly network: boolean;
  readonly allowedSites: readonly string[];
  readonly onReload: () => void;
}

function CardBody(props: CardBodyProps) {
  const toast = useToast();
  const confirm = useConfirm();
  const { employee } = props;

  const [displayName, setDisplayName] = useState(employee.displayName);
  const [phone, setPhone] = useState(employee.phone ?? '');
  const [password, setPasswordValue] = useState('');
  const [pin, setPinValue] = useState('');
  const [role, setRole] = useState<RoleName | ''>(props.roles[0] ?? '');
  const [siteId, setSiteId] = useState('');
  const [busy, setBusy] = useState(false);

  const canManage = allowsAnywhere(props.user, 'employee.manage');
  const canAudit = allowsAnywhere(props.user, 'audit.view');
  const isSelf = employee.id === props.user.id;

  const sites = props.network
    ? props.sites
    : props.sites.filter((s) => props.allowedSites.includes(s.id));

  function siteLabel(id: string | null): string {
    if (!id) return props.t('staff.form.site.network');
    return props.sites.find((s) => s.id === id)?.code ?? '—';
  }

  async function act(run: () => Promise<unknown>, message?: string): Promise<void> {
    setBusy(true);
    try {
      await run();
      if (message) toast.ok(message);
      props.onReload();
    } catch (err) {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card title={props.t('staff.card.profile')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={props.t('staff.form.name')}>
            <TextInput
              value={displayName}
              disabled={!canManage}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </Field>
          <Field label={props.t('staff.form.phone')}>
            <TextInput value={phone} disabled={!canManage} onChange={(e) => setPhone(e.target.value)} />
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Id value={employee.id} />
          <StatusBadge tone={employee.status === 'active' ? 'ok' : 'warn'}>
            {props.t(`staff.status.${employee.status}` as never)}
          </StatusBadge>
          {canManage ? (
            <Button
              tone="primary"
              size="sm"
              disabled={busy}
              onClick={() =>
                void act(
                  () =>
                    updateEmployee(employee.id, {
                      displayName: displayName.trim(),
                      phone: phone.trim() || null,
                    }),
                  props.t('staff.card.saved'),
                )
              }
            >
              {props.t('staff.card.save')}
            </Button>
          ) : null}
        </div>
      </Card>

      <Card title={props.t('staff.card.roles')}>
        {employee.roles.length === 0 ? (
          <Notice tone="warn">{props.t('staff.roles.empty')}</Notice>
        ) : (
          <ul className="flex flex-col gap-2">
            {employee.roles.map((r) => (
              <li
                key={`${r.role}:${r.siteId ?? 'net'}`}
                className="flex items-center justify-between gap-3 border-b border-line pb-2 last:border-0"
              >
                <span>
                  {props.shellT(`role.${r.role}` as ShellKey)} · {siteLabel(r.siteId)}
                </span>
                {canManage && !isSelf ? (
                  <Button
                    tone="danger"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void (async () => {
                        const ok = await confirm({
                          title: props.t('staff.roles.revokeConfirm'),
                          body: `${props.shellT(`role.${r.role}` as ShellKey)} · ${siteLabel(r.siteId)}`,
                          tone: 'danger',
                          consequences: [props.t('staff.roles.revokeEffect')],
                        });
                        if (ok) await act(() => revokeRole(employee.id, r), props.t('staff.roles.revoked'));
                      })()
                    }
                  >
                    {props.t('staff.roles.revoke')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {canManage && !isSelf ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <Field label={props.t('staff.form.role')}>
              <Select value={role} onChange={(e) => setRole(e.target.value as RoleName)}>
                {props.roles.map((r) => (
                  <option key={r} value={r}>
                    {props.shellT(`role.${r}` as ShellKey)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={props.t('staff.form.site')}>
              <Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                {props.network ? (
                  <option value="">{props.t('staff.form.site.network')}</option>
                ) : null}
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} · {s.name}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="flex items-end">
              <Button
                disabled={busy || !role}
                onClick={() =>
                  void act(
                    () => grantRole(employee.id, { role: role as RoleName, siteId: siteId || null }),
                    props.t('staff.roles.granted'),
                  )
                }
              >
                {props.t('staff.roles.add')}
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      <Card title={props.t('staff.card.account')}>
        {!employee.userId ? (
          <Notice tone="warn">{props.t('staff.account.none')}</Notice>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-ink-muted">
              <span>
                {props.t('staff.col.login')}: <code className="font-mono">{employee.login}</code>
              </span>
              <span>
                {props.t('staff.col.password')}: <Moment iso={employee.passwordChangedAt} />
              </span>
              <span>
                {props.t('staff.col.totp')}:{' '}
                {employee.totpConfirmed ? props.t('staff.totp.on') : props.t('staff.totp.off')}
              </span>
            </div>

            {canManage ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={props.t('staff.account.password')} hint={props.t('staff.form.passwordHint')}>
                    <TextInput
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPasswordValue(e.target.value)}
                    />
                  </Field>
                  <Field label={props.t('staff.account.pin')} hint={props.t('staff.form.pinHint')}>
                    <TextInput
                      inputMode="numeric"
                      value={pin}
                      onChange={(e) => setPinValue(e.target.value)}
                    />
                  </Field>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    disabled={busy || password.length === 0}
                    onClick={() =>
                      void (async () => {
                        const ok = await confirm({
                          title: props.t('staff.account.passwordConfirm'),
                          body: employee.displayName,
                          consequences: [
                            props.t('staff.account.passwordEffect1'),
                            props.t('staff.account.passwordEffect2'),
                          ],
                        });
                        if (!ok) return;
                        await act(
                          () => setPassword(employee.id, password),
                          props.t('staff.account.passwordDone'),
                        );
                        setPasswordValue('');
                      })()
                    }
                  >
                    {props.t('staff.account.password')}
                  </Button>

                  <Button
                    disabled={busy || pin.trim().length < 4}
                    onClick={() =>
                      void act(() => setPin(employee.id, pin.trim()), props.t('staff.account.pinDone')).then(
                        () => setPinValue(''),
                      )
                    }
                  >
                    {props.t('staff.account.pin')}
                  </Button>

                  {employee.lockedUntil ? (
                    <Button
                      disabled={busy}
                      onClick={() => void act(() => unlock(employee.id), props.t('staff.account.unlocked'))}
                    >
                      {props.t('staff.account.unlock')}
                    </Button>
                  ) : null}

                  <Button
                    disabled={busy}
                    onClick={() =>
                      void act(async () => {
                        const result = await revokeSessions(employee.id);
                        toast.ok(props.t('staff.account.sessionsDone', { count: result.revoked }));
                      })
                    }
                  >
                    {props.t('staff.account.sessions')}
                  </Button>

                  {employee.totpConfirmed ? (
                    <Button
                      tone="danger"
                      disabled={busy}
                      onClick={() =>
                        void (async () => {
                          const ok = await confirm({
                            title: props.t('staff.account.totpResetConfirm'),
                            body: employee.displayName,
                            tone: 'danger',
                            consequences: [
                              props.t('staff.account.totpResetEffect1'),
                              props.t('staff.account.totpResetEffect2'),
                            ],
                          });
                          if (ok)
                            await act(
                              () => resetTotp(employee.id),
                              props.t('staff.account.totpResetDone'),
                            );
                        })()
                      }
                    >
                      {props.t('staff.account.totpReset')}
                    </Button>
                  ) : null}
                </div>
              </>
            ) : null}
          </>
        )}
      </Card>

      {canManage && !isSelf ? (
        <Card title={props.t('staff.status.change')}>
          <div className="flex flex-wrap gap-2">
            {employee.status !== 'active' ? (
              <Button
                disabled={busy}
                onClick={() => void changeStatus('active')}
              >
                {props.t('staff.status.restore')}
              </Button>
            ) : (
              <Button disabled={busy} onClick={() => void changeStatus('suspended')}>
                {props.t('staff.status.suspend')}
              </Button>
            )}

            {employee.status !== 'dismissed' ? (
              <Button tone="danger" disabled={busy} onClick={() => void dismiss()}>
                {props.t('staff.status.dismiss')}
              </Button>
            ) : null}
          </div>
        </Card>
      ) : null}

      {/*
        * Что делали с учётной записью: выдача и снятие ролей, сброс пароля и
        * второго фактора, блокировка. Всё это писалось в журнал с первого дня
        * раздела и до сих пор нигде не показывалось.
        */}
      {canAudit ? (
        <AuditTrail entityId={employee.id} title={props.t('staff.card.history')} />
      ) : null}
    </div>
  );

  async function changeStatus(next: EmployeeStatus): Promise<void> {
    await act(() => setStatus(employee.id, next), props.t('staff.status.changed'));
  }

  async function dismiss(): Promise<void> {
    // Самое необратимое действие раздела: просим набрать слово, чтобы движение
    // руки не совпало с движением мысли — тот же приём, что у обезличивания ПДн.
    const ok = await confirm({
      title: props.t('staff.status.dismissConfirm'),
      body: employee.displayName,
      tone: 'danger',
      typeToConfirm: props.t('staff.status.dismissWord'),
      consequences: [
        props.t('staff.status.dismissEffect1'),
        props.t('staff.status.dismissEffect2'),
        props.t('staff.status.dismissEffect3'),
      ],
    });
    if (ok) await changeStatus('dismissed');
  }
}
